import { defineStore } from 'pinia';
import axios from 'axios';
import { isEqual } from 'lodash';

export const useAiStore = defineStore('ai', {
  state: () => ({
    // AI Side Panel State
    sidePanel: {
      isOpen: false,
      content: null,
      context: null,
      contextFactory: null, // Lazy factory: called at send-time to build inner context object
      toolSchemasFactory: null, // Lazy factory: called at send-time to get canonical tool schemas [{name, description, parameters}]
      limitStatus: null, // Current conversation limit status
      isPinned: true, // Whether the panel is pinned (pushes content) or overlay
      panelWidth: 500, // User-resized panel width in pixels
      contextReady: false, // True once page data is loaded and agent may safely act
      anchorRouteName: null, // The parent route name of the component that registered context (used to suppress resets on sibling sub-tab navigation)
    },
    // Scroll position cache for the AI conversation panel (in-memory only, not persisted)
    panelScrollPositions: {},
    // AI Assistant State
    assistant: {
      currentConversationId: null,
      messages: [],
      isLoading: false,
      lastSentContext: null, // Track last sent context to detect changes
      // mode removed — the assistant is always in unified mode. Mutating tool calls
      // display a confirmation prompt before executing; read-only tools run silently.
      conversationFeedback: null, // { rating: 'up'|'down', comment, submittedAt }
      messageFeedback: {}, // Keyed by messageId: { rating: 'up'|'down', submittedAt }
      isCancelled: false, // True when the user has requested to stop the agent action chain
      isHandlingAgentAction: false, // True while a dispatched action handler or confirmation gate is active
    },
    // Pending native tool call dispatched by the LLM — consumed by the active page's composable watcher.
    // Only one tool call is ever pending at a time (parallel_tool_calls: false enforced on the server).
    // Shape: { id: string, name: string, arguments: object } | null
    pendingToolCall: null,

    // Snapshot of the tool schemas that were active when pendingToolCall was dispatched.
    // Stored alongside the tool call so submitToolResult can send the same schemas for chained
    // calls, even if page navigation has since replaced or nulled out toolSchemasFactory
    // (e.g. the 'navigate' tool causes a route change before the result is submitted).
    // Shape: Array<schema> | null  (schemas include workingLabel for client display)
    pendingToolCallSchemas: null,

    // Pending action confirmation — set when a mutating tool call requires user approval before
    // executing. The composable watcher awaits a Promise that resolves when the user responds.
    // Shape: { toolName: string, displayLabel: string, resolve: Function } | null
    // Note: intentionally not persisted — a pending confirmation is always in-flight state.
    pendingConfirmation: null,

    // Session-scoped approval/exclusion state (in-memory only, clears on page refresh).
    // Keyed by tool name. Separate from pendingConfirmation to allow independent querying.
    sessionApprovals: [], // Tool names the user has approved for the whole session
    sessionAllActionsApproved: false, // User approved all mutating tools for the session
    sessionExclusions: [], // Tool names the user has blocked for the whole session
    sessionAllActionsBlocked: false, // User blocked all mutating tools for the session
  }),

  getters: {
    /**
     * Check if the AI side panel is currently open
     */
    isSidePanelOpen: state => state.sidePanel.isOpen,

    /**
     * Get the current AI side panel context
     */
    sidePanelContext: state => state.sidePanel.context,

    /**
     * Get the lazy context factory function (called at send-time to build inner context)
     */
    sidePanelContextFactory: state => state.sidePanel.contextFactory,

    /**
     * Check if the AI side panel is pinned (pushes page content aside)
     */
    isSidePanelPinned: state => state.sidePanel.isPinned,

    /**
     * Get the current panel width in pixels
     */
    sidePanelWidth: state => state.sidePanel.panelWidth,
  },

  actions: {
    /**
     * Records the authenticated user's acceptance of the AI terms of use.
     * Updates the context store so the UI reacts immediately without a page reload.
     */
    async acceptAiTerms() {
      const { useContextStore } = await import('@/stores/context');
      const contextStore = useContextStore();

      const response = await axios.post('/api/ai-assistant/accept-terms');
      contextStore.aiTermsAcceptedAt = response.data.acceptedAt;
    },

    /**
     * Open the AI side panel
     */
    openSidePanel() {
      this.sidePanel.isOpen = true;
    },

    /**
     * Close the AI side panel without clearing retained state
     */
    closeSidePanel() {
      this.sidePanel.isOpen = false;
    },

    /**
     * Toggle the AI side panel open/closed state
     */
    toggleSidePanel() {
      if (this.sidePanel.isOpen) {
        this.closeSidePanel();
      } else {
        this.openSidePanel();
      }
    },

    /**
     * Register the current page's context with the AI side panel.
     * Called from each page's mounted() hook.
     * Does not affect isOpen — panel open/closed state is global and persists across navigation.
     * @param {Object} context - The AI insights context for the current page
     */
    enableSidePanel(context, anchorRouteName = null) {
      this.sidePanel.context = context;
      if (anchorRouteName) {
        this.sidePanel.anchorRouteName = anchorRouteName;
      }
    },

    /**
     * Reset page-specific context when navigating away.
     * Called by the route watcher in use-ai-side-panel.js (via AiSidePanel.vue).
     * Does NOT close the panel — open/closed state is global.
     */
    resetPageContext() {
      this.sidePanel.context = null;
      this.sidePanel.contextFactory = null;
      this.sidePanel.toolSchemasFactory = null;
      this.sidePanel.contextReady = false;
      this.sidePanel.anchorRouteName = null;
    },

    /**
     * Update the AI side panel context (e.g., when data changes on the page)
     * @param {Object} context - New context object
     */
    updateSidePanelContext(context) {
      this.sidePanel.context = context;
    },

    /**
     * Register a lazy factory that returns the inner context object when called.
     * Called at message-send time to build the current page's context for the POST body.
     * @param {Function|null} fn - Factory function () => object, or null to clear
     */
    setSidePanelContextFactory(fn) {
      this.sidePanel.contextFactory = fn;
    },

    /**
     * Register a lazy factory that returns the canonical tool schemas when called.
     * Called at message-send time to get the current tool schemas for the POST body.
     * @param {Function|null} fn - Factory function () => Array<{name, description, parameters}>, or null to clear
     */
    setToolSchemasFactory(fn) {
      this.sidePanel.toolSchemasFactory = fn;
    },

    /**
     * Set the pending native tool call for the composable watcher to execute.
     * @param {{ id: string, name: string, arguments: object }} toolCall
     */
    setPendingToolCall(toolCall) {
      this.assistant.isHandlingAgentAction = true;
      this.pendingToolCall = toolCall;
    },

    /**
     * Request the user to confirm a mutating tool call before execution.
     * Returns a Promise that resolves with the user's choice:
     *   'yes-once'            – approve this single invocation
     *   'yes-session-action'  – approve this tool for the whole session
     *   'yes-session-all'     – approve all mutating tools for the session
     *   'no-once'             – decline this single invocation (tool stays in schemas)
     *   'no-session-action'   – decline this tool for the whole session
     *   'no-session-all'      – decline all mutating tools for the session
     *   'cancel'              – request was cancelled (e.g. conversation cleared)
     * @param {string} toolName
     * @param {string} displayLabel – human-readable sentence shown in the prompt UI
     * @returns {Promise<string>}
     */
    requestToolConfirmation(toolName, displayLabel) {
      return new Promise(resolve => {
        this.pendingConfirmation = { toolName, displayLabel, resolve };
      });
    },

    /**
     * Resolve the pending confirmation with the user's choice.
     * Called by the AiToolConfirmationPrompt component when the user clicks a button.
     * @param {'yes-once'|'yes-session-action'|'yes-session-all'|'no-once'|'no-session-action'|'no-session-all'} choice
     */
    resolveToolConfirmation(choice) {
      if (!this.pendingConfirmation) return;
      const { resolve } = this.pendingConfirmation;
      this.pendingConfirmation = null;
      resolve(choice);
    },

    /** Approve a specific tool for the whole session. */
    addSessionApproval(toolName) {
      if (!this.sessionApprovals.includes(toolName)) {
        this.sessionApprovals.push(toolName);
      }
    },

    /** Block a specific tool for the whole session. */
    addSessionExclusion(toolName) {
      if (!this.sessionExclusions.includes(toolName)) {
        this.sessionExclusions.push(toolName);
      }
    },

    /** Approve all mutating tools for the whole session. */
    setSessionAllActionsApproved() {
      this.sessionAllActionsApproved = true;
    },

    /** Block all mutating tools for the whole session. */
    setSessionAllActionsBlocked() {
      this.sessionAllActionsBlocked = true;
    },

    /**
     * Clear the pending tool call once it has been claimed by the composable watcher.
     * Called synchronously before any await to prevent duplicate handling.
     */
    clearPendingToolCall() {
      this.pendingToolCall = null;
      // Note: pendingToolCallSchemas is intentionally NOT cleared here.
      // clearPendingToolCall is called synchronously before the tool handler runs;
      // the snapshot must survive until submitToolResult is called.
      // It is cleared in submitToolResult when the chain ends (success or error).
    },

    /**
     * Add a chart message to the conversation.
     * Called by useAiAgentActions when the render_chart tool is invoked.
     * Promotes any active placeholder to a completed step before inserting the chart
     * so the "Drawing chart..." indicator resolves correctly.
     * @param {object} chartOptions - Highcharts options object provided by the LLM
     */
    addChartMessage(chartOptions) {
      const lastMsg = this.assistant.messages[this.assistant.messages.length - 1];
      if (lastMsg?.isPlaceholder) {
        lastMsg.isPlaceholder = false;
        lastMsg.isCompletedStep = true;
        lastMsg.stepOutcome = 'success';
      }
      this.assistant.messages.push({
        role: 'assistant',
        isChart: true,
        chartData: chartOptions,
        timestamp: new Date(),
      });
    },

    /**
     * Add a table message to the conversation.
     * Called by useAiAgentActions when the render_table tool is invoked.
     * Promotes any active placeholder to a completed step before inserting the table.
     * @param {{ title: string, columns: Array<{field, headerName, type?}>, rows: object[] }} tableData
     */
    addTableMessage(tableData) {
      const lastMsg = this.assistant.messages[this.assistant.messages.length - 1];
      if (lastMsg?.isPlaceholder) {
        lastMsg.isPlaceholder = false;
        lastMsg.isCompletedStep = true;
        lastMsg.stepOutcome = 'success';
      }
      this.assistant.messages.push({
        role: 'assistant',
        isTable: true,
        tableData,
        timestamp: new Date(),
      });
    },

    /**
     * Signal that the current page's data has loaded and the agent may safely act.
     * Call this after all data required by buildInnerContext() has been fetched.
     * @param {boolean} ready
     */
    setContextReady(ready) {
      this.sidePanel.contextReady = ready;
    },

    /**
     * Update only the section field on the side panel context.
     * Used by pages (e.g. ScenarioAssortments) where the section changes dynamically
     * without needing to rebuild the entire inner context eagerly.
     * @param {string} section - New section identifier
     */
    updateSidePanelSection(section) {
      if (this.sidePanel.context) {
        this.sidePanel.context.section = section;
      }
    },

    /**
     * Set whether the AI side panel is pinned
     * @param {boolean} pinned
     */
    setPinned(pinned) {
      this.sidePanel.isPinned = pinned;
    },

    /**
     * Set the panel width (clamped between 350–800px)
     * @param {number} width - Width in pixels
     */
    setPanelWidth(width) {
      this.sidePanel.panelWidth = Math.min(600, Math.max(350, width));
    },

    /**
     * Save scroll position for the AI conversation panel
     * @param {string} contextId - Cache key
     * @param {number} scrollTop - Scroll position to save
     */
    saveScrollPosition(contextId, scrollTop) {
      this.panelScrollPositions[contextId] = scrollTop;
    },

    /**
     * Get saved scroll position for the AI conversation panel
     * @param {string} contextId - Cache key
     * @returns {number} - Saved scroll position (0 if not found)
     */
    getScrollPosition(contextId) {
      return this.panelScrollPositions[contextId] || 0;
    },

    // AI Assistant Actions
    /**
     * Check if the current context has changed from what was last sent
     * @returns {boolean} - True if context has changed
     */
    hasContextChanged() {
      // No active conversation — nothing to sync
      if (!this.assistant.currentConversationId) {
        return false;
      }

      // Context was just cleared (navigated to unintegrated page) but server still has old context
      if (!this.sidePanel.context && this.assistant.lastSentContext) {
        return true;
      }

      // No context on either side — nothing to do
      if (!this.sidePanel.context) {
        return false;
      }

      // No last sent context = first time, needs update
      if (!this.assistant.lastSentContext) {
        return true;
      }

      // Compare section
      if (this.sidePanel.context.section !== this.assistant.lastSentContext.section) {
        return true;
      }

      const currentContext = this.sidePanel.contextFactory?.() || {};
      const lastContext = this.assistant.lastSentContext.context || {};

      // Use deep equality rather than JSON.stringify to avoid false positives from
      // differing property insertion order in dynamically-built context objects.
      return !isEqual(currentContext, lastContext);
    },

    /**
     * Update the assistant's context (lazy update - only called when sending messages)
     * @returns {Promise<void>}
     */
    async updateAssistantContext() {
      if (!this.assistant.currentConversationId) {
        return;
      }

      // Null context means the user has navigated to a page with no AI integration.
      // Send section: null so the server clears contextData and uses only the base prompt.
      const isClearing = !this.sidePanel.context;
      const section = isClearing ? null : this.sidePanel.context.section;
      const context = isClearing ? {} : this.sidePanel.contextFactory?.() || {};

      try {
        await axios.put(
          `/api/ai-assistant/conversations/${this.assistant.currentConversationId}/context`,
          { section, context }
        );

        // Clear or update last sent context to match what the server now holds
        this.assistant.lastSentContext = isClearing ? null : { section, context: { ...context } };
      } catch (error) {
        console.error('Error updating assistant context:', error);

        // If conversation not found or forbidden, clear the conversation
        if (error.response && (error.response.status === 403 || error.response.status === 404)) {
          console.warn('Conversation no longer valid, clearing assistant state');
          this.clearAssistantConversation();
        }

        throw error;
      }
    },

    /**
     * Build the section/context payload from the current side panel state.
     * Used by both conversation creation methods to avoid duplication.
     * @returns {{ section: string|null, context: Object }}
     */
    buildConversationPayload() {
      const section = this.sidePanel.context?.section || null;
      const context = this.sidePanel.contextFactory?.() || {};
      return { section, context };
    },

    /**
     * Create a new AI Assistant conversation (lazy init on first message send).
     * The caller may pass extra fields (title, scenarioId, workpackageId) via payload.
     * @param {Object} payload - Additional conversation creation fields
     * @returns {Promise<Object>} - Created conversation
     */
    async createAssistantConversation(payload) {
      try {
        const { section, context } = this.buildConversationPayload();

        const response = await axios.post('/api/ai-assistant/conversations', {
          ...payload,
          section,
          context,
        });

        this.assistant.currentConversationId = response.data._id;
        this.assistant.messages = [];
        this.assistant.lastSentContext = section ? { section, context: { ...context } } : null;

        return response.data;
      } catch (error) {
        console.error('Error creating assistant conversation:', error);
        throw error;
      }
    },

    /**
     * Send a message to the AI Assistant
     * @param {string} message - Message to send
     * @returns {Promise<Object>} - Response from assistant
     */
    async sendAssistantMessage(message, { isActionFeedback = false } = {}) {
      if (!this.assistant.currentConversationId) {
        throw new Error('No active conversation. Create a conversation first.');
      }

      // Reset cancellation flag on every call — fresh user messages start a clean chain,
      // and the summary feedback call after a stop also clears it before executing.
      this.assistant.isCancelled = false;
      this.assistant.isHandlingAgentAction = false;
      this.assistant.isLoading = true;

      try {
        // Check if context has changed - if so, update context first
        if (this.hasContextChanged()) {
          // Update context in backend (sends context as separate user message to thread)
          await this.updateAssistantContext();
        }

        // Add user message to local state (action feedback messages are hidden from the UI)
        this.assistant.messages.push({
          role: 'user',
          content: message,
          timestamp: new Date(),
          ...(isActionFeedback && { isActionFeedback: true }),
        });

        // Resolve tool schemas lazily at send-time (dynamic — navigate schemas change as routes mount).
        // workingLabel is a client-side-only field used for the placeholder bubble — strip it before
        // sending to the server so it is never included in the OpenAI tools payload.
        const allSchemas = this.sidePanel.toolSchemasFactory?.() || [];
        const workingLabels = Object.fromEntries(
          allSchemas.filter(s => s.workingLabel).map(s => [s.name, s.workingLabel])
        );
        const clientToolSchemas = allSchemas.map(({ workingLabel: _wl, ...schema }) => schema);

        // Send to API
        const response = await axios.post(
          `/api/ai-assistant/conversations/${this.assistant.currentConversationId}/messages`,
          {
            message,
            ...(isActionFeedback && { isActionFeedback: true }),
            ...(clientToolSchemas.length > 0 && { clientToolSchemas }),
          }
        );

        // Update limit status from response
        this.sidePanel.limitStatus = response.data.limitStatus;

        // Only add the assistant message if there is actual text content.
        // When the LLM responds with a tool call and no text, response is '' — don't push an empty bubble.
        if (response.data.response) {
          this.assistant.messages.push({
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date(),
            messageId: response.data.messageId || null,
          });
        }

        if (response.data.pendingToolCall) {
          // LLM made a tool call (possibly with accompanying preamble text) — push a placeholder so the
          // user sees the working label instead of the chat going silent while the tool runs.
          // This is a separate block from the text check above so both can render when the LLM
          // writes preamble text before dispatching a tool call.
          this.assistant.messages.push({
            role: 'assistant',
            content: '',
            isPlaceholder: true,
            workingLabel: workingLabels[response.data.pendingToolCall.name] || null,
            timestamp: new Date(),
          });
        }

        // Dispatch the pending tool call for the composable watcher on the active page to execute.
        // Snapshot schemas now — toolSchemasFactory may be replaced by the incoming page's
        // tools before submitToolResult is called (e.g. when the 'navigate' tool fires).
        if (response.data.pendingToolCall) {
          this.pendingToolCallSchemas = allSchemas;
          this.setPendingToolCall(response.data.pendingToolCall);
        }

        return response.data;
      } catch (error) {
        console.error('Error sending assistant message:', error);

        // Check if conversation limit reached
        if (error.response?.data?.code === 'CONVERSATION_LIMIT_REACHED') {
          // Set blocking error status
          this.sidePanel.limitStatus = {
            severity: 'error',
            message:
              error.response.data.error ||
              'Conversation limit reached. Please start a new conversation.',
            isBlocking: true,
          };

          // Remove the user message we optimistically added
          this.assistant.messages.pop();

          // Don't add generic error message for limit reached
          throw error;
        }

        // Add error message to conversation for other errors only
        this.assistant.messages.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
          isError: true,
        });
        throw error;
      } finally {
        this.assistant.isLoading = false;
      }
    },

    /**
     * Submit a client-side tool result to the server to resume the LLM tool calling loop.
     * Called by the composable watcher after a client tool handler has executed.
     * @param {{ id: string, name: string, result: string, imageData?: string }} toolResult
     *   imageData is an optional base64 PNG data URI (from capture_screenshot). When present it is
     *   forwarded to the server to be attached to the assistant model call. It is never stored in
     *   conversation history — only the plain `result` string is persisted.
     * @returns {Promise<Object>} - Response from the server (may include another pendingToolCall)
     */
    async submitToolResult(toolResult) {
      if (!this.assistant.currentConversationId) {
        throw new Error('No active conversation.');
      }

      this.assistant.isLoading = true;

      // Sync context before submitting the tool result — navigation may have changed the active
      // section between when the tool call was dispatched and when the result is returned
      // (e.g. the navigate tool itself triggers a page transition before resolving).
      // This mirrors the same check in sendAssistantMessage so the server always has current context.
      if (this.hasContextChanged()) {
        await this.updateAssistantContext();
      }

      // Use the snapshotted schemas from when the pendingToolCall was dispatched, falling back to
      // the live factory. This ensures the LLM sees the same tools across chained calls even when
      // page navigation (e.g. the 'navigate' tool) has replaced toolSchemasFactory in the interim.
      const resolvedSchemas =
        this.pendingToolCallSchemas ?? this.sidePanel.toolSchemasFactory?.() ?? [];
      const clientToolSchemas = resolvedSchemas.map(({ workingLabel: _wl, ...schema }) => schema);

      // Separate imageData from the core toolResult before sending — the server
      // expects { toolResult: { id, name, result }, imageData?, clientToolSchemas? }.
      // imageData is forwarded to the assistant model but never persisted in history.
      const { imageData, outcome, ...coreToolResult } = toolResult;

      try {
        const response = await axios.post(
          `/api/ai-assistant/conversations/${this.assistant.currentConversationId}/tool-results`,
          {
            toolResult: coreToolResult,
            ...(imageData && { imageData }),
            ...(clientToolSchemas.length > 0 && { clientToolSchemas }),
          }
        );

        // Update limit status
        if (response.data.limitStatus) {
          this.sidePanel.limitStatus = response.data.limitStatus;
        }

        // Append the final assistant message. Promote any active placeholder to a
        // completed-step indicator so the last action label stays visible, then push
        // the answer as a new message beneath it.
        if (response.data.response) {
          const lastMsg = this.assistant.messages[this.assistant.messages.length - 1];
          if (lastMsg?.isPlaceholder) {
            lastMsg.isPlaceholder = false;
            lastMsg.isCompletedStep = true;
            lastMsg.stepOutcome = outcome || 'success';
          }
          this.assistant.messages.push({
            role: 'assistant',
            content: response.data.response,
            timestamp: new Date(),
            messageId: response.data.messageId || null,
          });
        }
        // Chain to another client tool call if present
        if (response.data.pendingToolCall) {
          // Each tool in the chain gets its own "Working..." box. Mark the current
          // placeholder as a completed step and push a new one for the next tool.
          // Use the same schema snapshot (resolvedSchemas) for working labels — toolSchemasFactory
          // may already point at a different page after a navigate tool call.
          const chainWorkingLabels = Object.fromEntries(
            resolvedSchemas.filter(s => s.workingLabel).map(s => [s.name, s.workingLabel])
          );
          const lastMsg = this.assistant.messages[this.assistant.messages.length - 1];
          if (lastMsg?.isPlaceholder) {
            lastMsg.isPlaceholder = false;
            lastMsg.isCompletedStep = true;
            lastMsg.stepOutcome = outcome || 'success';
          }
          this.assistant.messages.push({
            role: 'assistant',
            content: '',
            isPlaceholder: true,
            workingLabel: chainWorkingLabels[response.data.pendingToolCall.name] || null,
            timestamp: new Date(),
          });
          // Keep pendingToolCallSchemas — the same schema set applies to the next call in the chain.
          this.setPendingToolCall(response.data.pendingToolCall);
        } else {
          // No further tool calls — the chain is complete. Clear the handling flag and schema snapshot.
          this.assistant.isHandlingAgentAction = false;
          this.pendingToolCallSchemas = null;
        }

        return response.data;
      } catch (error) {
        console.error('Error submitting tool result:', error);

        // Check for stale/mismatched tool call ID (409)
        if (error.response?.status === 409) {
          console.warn('[AI] Tool call ID mismatch — stale pending tool call, ignoring.');
          this.clearPendingToolCall();
          this.pendingToolCallSchemas = null;
          this.assistant.isHandlingAgentAction = false;
          return;
        }

        // Always unblock the UI — the chain cannot continue after an error.
        this.assistant.isHandlingAgentAction = false;
        this.pendingToolCallSchemas = null;

        // Resolve any active "Working..." placeholder to a failed completed step so the
        // spinner disappears and the step label remains visible.
        const lastMsg = this.assistant.messages[this.assistant.messages.length - 1];
        if (lastMsg?.isPlaceholder) {
          lastMsg.isPlaceholder = false;
          lastMsg.isCompletedStep = true;
          lastMsg.stepOutcome = 'failed';
        }

        // Check if the conversation limit was reached during the tool call loop.
        if (error.response?.data?.code === 'CONVERSATION_LIMIT_REACHED') {
          this.sidePanel.limitStatus = {
            severity: 'error',
            message:
              error.response.data.error ||
              'Conversation limit reached. Please start a new conversation.',
            isBlocking: true,
          };
          throw error;
        }

        // For all other errors push a visible error bubble so the user is not left wondering
        // what happened, then re-throw so the composable can log it.
        this.assistant.messages.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
          isError: true,
        });
        throw error;
      } finally {
        this.assistant.isLoading = false;
      }
    },

    async getConversation(conversationId) {
      try {
        const response = await axios.get(`/api/ai-assistant/conversations/${conversationId}`);
        this.assistant.currentConversationId = conversationId;
        this.assistant.messages = response.data.messages || [];
        return response.data;
      } catch (error) {
        console.error('Error getting assistant conversation:', error);
        throw error;
      }
    },

    /**
     * Clear current assistant conversation
     */
    clearAssistantConversation() {
      this.assistant.currentConversationId = null;
      this.assistant.messages = [];
      this.assistant.isLoading = false;
      this.assistant.isCancelled = false;
      this.assistant.isHandlingAgentAction = false;
      this.assistant.lastSentContext = null;
      this.assistant.conversationFeedback = null;
      this.assistant.messageFeedback = {};
      this.pendingToolCall = null;
      this.pendingToolCallSchemas = null;
      // Cancel any pending confirmation — resolve with 'cancel' so the awaiting watcher unblocks.
      if (this.pendingConfirmation) {
        const { resolve } = this.pendingConfirmation;
        this.pendingConfirmation = null;
        resolve('cancel');
      }
    },

    /**
     * Request the agent action chain to stop after the current action completes.
     * The composable watcher checks this flag before submitting a tool result.
     */
    cancelCurrentRequest() {
      this.assistant.isCancelled = true;
      // Also resolve any pending confirmation with 'cancel' so the watcher unblocks immediately.
      if (this.pendingConfirmation) {
        const { resolve } = this.pendingConfirmation;
        this.pendingConfirmation = null;
        resolve('cancel');
      }
    },

    /**
     * Submit or overwrite feedback for the current conversation.
     * @param {'up'|'down'} rating
     * @param {string|null} comment
     */
    async submitConversationFeedback({ rating, comment = null }) {
      if (!this.assistant.currentConversationId) return;
      await axios.post(
        `/api/ai-assistant/conversations/${this.assistant.currentConversationId}/feedback`,
        { rating, comment }
      );
      this.assistant.conversationFeedback = { rating, comment, submittedAt: new Date() };
    },

    /**
     * Submit or overwrite thumbs feedback on a single assistant message.
     * @param {string} messageId  - OpenAI message ID stored on the message object
     * @param {'up'|'down'} rating
     */
    async submitMessageFeedback({ messageId, rating }) {
      if (!this.assistant.currentConversationId || !messageId) return;
      await axios.post(
        `/api/ai-assistant/conversations/${this.assistant.currentConversationId}/messages/${messageId}/feedback`,
        { rating }
      );
      this.assistant.messageFeedback = {
        ...this.assistant.messageFeedback,
        [messageId]: { rating, submittedAt: new Date() },
      };
    },

    /**
     * Create a new conversation, resetting all conversation state.
     * Called when the user explicitly starts fresh via the "New Conversation" button.
     * @returns {Promise<Object>} - Created conversation
     */
    async createNewConversation() {
      try {
        const { section, context } = this.buildConversationPayload();

        const response = await axios.post('/api/ai-assistant/conversations', {
          section,
          context,
        });

        this.assistant.currentConversationId = response.data._id;
        this.assistant.messages = [];
        this.assistant.isCancelled = false;
        this.assistant.isHandlingAgentAction = false;
        this.pendingToolCall = null;
        this.pendingToolCallSchemas = null;
        this.assistant.lastSentContext = section ? { section, context: { ...context } } : null;
        this.assistant.conversationFeedback = null;
        this.assistant.messageFeedback = {};
        this.sidePanel.limitStatus = null;
        // Cancel any pending confirmation so the awaiting watcher unblocks.
        if (this.pendingConfirmation) {
          const { resolve } = this.pendingConfirmation;
          this.pendingConfirmation = null;
          resolve('cancel');
        }

        return response.data;
      } catch (error) {
        console.error('Error creating new conversation:', error);
        throw error;
      }
    },
  },
  persist: {
    storage: localStorage,
    key: 'ai-store',
    // contextReady is intentionally excluded — it must always start false on a fresh session
    // assistant.mode removed — the assistant is always in unified mode.
    // sessionApprovals/sessionExclusions are intentionally excluded — they should reset on page refresh.
    paths: ['sidePanel.isOpen', 'sidePanel.isPinned', 'sidePanel.panelWidth'],
  },
});
